import React, { useEffect, useState } from "react";
import { useRefreshTick } from "@/lib/autoRefresh";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TrendingUp, Banknote, ArrowRightLeft, HandCoins } from "lucide-react";

const iso = (d) => d.toLocaleDateString("en-CA");
export const PERIODS = {
  today: () => { const t = new Date(); return [iso(t), iso(t)]; },
  week: () => { const t = new Date(); const s = new Date(t); s.setDate(t.getDate() - ((t.getDay() + 6) % 7)); return [iso(s), iso(t)]; },
  month: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(t)]; },
};
export const LABELS = { today: "Hari Ini", week: "Minggu Ini", month: "Bulan Ini", custom: "Custom" };

function Section({ icon: Icon, title, total, rows, testId }) {
  return (
    <div className="py-2 border-t first:border-t-0" data-testid={testId}>
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {title}</span>
        <span className="font-mono font-semibold" data-testid={`${testId}-total`}>{formatRupiah(total)}</span>
      </div>
      {rows?.length > 0 && (
        <div className="mt-1 pl-5 space-y-0.5">
          {rows.map((r) => (
            <div key={r.label} className="flex justify-between text-[11px] text-muted-foreground">
              <span className="font-mono">{r.label}</span><span className="font-mono">{formatRupiah(r.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function RevenuePerPtPanel() {
  const [period, setPeriod] = useState("month");
  const [custom, setCustom] = useState(PERIODS.month());
  const [data, setData] = useState(null);
  const [start, end] = period === "custom" ? custom : PERIODS[period]();

  const tick = useRefreshTick();
  useEffect(() => {
    api.get("/dashboard/revenue-per-pt", { params: { start_date: start || undefined, end_date: end || undefined } })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
  }, [start, end, tick]);

  return (
    <Card className="p-4" data-testid="revenue-pt-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-brand" />
          <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Pendapatan Per PT</h3>
          <span className="text-xs text-muted-foreground">Total <span className="font-mono font-semibold text-brand" data-testid="revenue-pt-total">{formatRupiah(data?.total || 0)}</span></span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border p-0.5 bg-secondary" data-testid="revenue-pt-period">
            {Object.entries(LABELS).map(([k, l]) => (
              <button key={k} onClick={() => setPeriod(k)} data-testid={`revenue-period-${k}`}
                className={`px-2.5 py-1 text-xs rounded transition-colors ${period === k ? "bg-white shadow-sm font-semibold text-brand" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
          {period === "custom" && (
            <div className="flex items-center gap-1">
              <Input type="date" value={custom[0]} onChange={(e) => setCustom([e.target.value, custom[1]])} className="h-8 w-36 text-xs" data-testid="revenue-custom-start" />
              <span className="text-xs">–</span>
              <Input type="date" value={custom[1]} onChange={(e) => setCustom([custom[0], e.target.value])} className="h-8 w-36 text-xs" data-testid="revenue-custom-end" />
            </div>
          )}
        </div>
      </div>
      {!data ? <div className="h-4 rounded-md skeleton-shimmer" /> : data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="revenue-pt-empty">Belum ada pendapatan pada periode ini.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {data.items.map((g) => (
            <div key={g.company_id} className="rounded-xl border bg-white p-3.5" data-testid={`revenue-pt-card-${g.company_id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="font-semibold text-sm truncate">{g.company_name}</p>
                <p className="font-mono font-bold text-brand" data-testid={`revenue-pt-card-${g.company_id}-total`}>{formatRupiah(g.total)}</p>
              </div>
              <Section icon={Banknote} title="Cash Masuk" total={g.cash_in} testId={`revenue-${g.company_id}-cash`}
                rows={[{ label: "Penjualan Cash", amount: g.cash_sales }, { label: "Pembayaran Piutang (Cash)", amount: g.cash_piutang }]} />
              <div className="pl-5 pb-2 space-y-0.5 text-[11px]" data-testid={`revenue-${g.company_id}-cash-status`}>
                <div className="flex justify-between text-emerald-700 font-semibold"><span>Tunai Pusat (diterima)</span><span className="font-mono" data-testid={`revenue-${g.company_id}-cash-received`}>{formatRupiah(g.cash_pusat ?? g.cash_received)}</span></div>
                <div className="flex justify-between text-amber-800 font-semibold"><span>Tunai Pemohon (dipegang)</span><span className="font-mono" data-testid={`revenue-${g.company_id}-cash-pemohon`}>{formatRupiah(g.cash_pemohon || 0)}</span></div>
                <div className="flex justify-between text-amber-700 pl-3"><span>• Menunggu Konfirmasi</span><span className="font-mono" data-testid={`revenue-${g.company_id}-cash-pending`}>{formatRupiah(g.cash_pending)}</span></div>
                <div className="flex justify-between text-rose-600 pl-3"><span>• Belum Disetorkan</span><span className="font-mono" data-testid={`revenue-${g.company_id}-cash-notdeposited`}>{formatRupiah(g.cash_not_deposited)}</span></div>
              </div>
              <Section icon={ArrowRightLeft} title="Transfer" total={g.transfer_total} rows={g.transfer} testId={`revenue-${g.company_id}-transfer`} />
              <Section icon={HandCoins} title="Pembayaran Piutang" total={g.piutang_total} rows={g.piutang} testId={`revenue-${g.company_id}-piutang`} />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
